import { type ReactNode, forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import { Breadcrumbs, type BreadcrumbItem } from "@/components/ui/breadcrumbs";
import { StatusBadge, type StatusBadgeProps } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent, type TabItem } from "@/components/ui/tabs";
import { zOverlay, zModal } from "@/lib/z";
import { cn } from "@/lib/cn";

/**
 * Drawer — side sheet built on @radix-ui/react-dialog (DESIGN-SYSTEM §5).
 * Focus-trap, Esc, scroll-lock come from Radix Dialog. side: right (default) |
 * bottom; size: sm | md | lg | full.
 */
export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;
export const DrawerPortal = DialogPrimitive.Portal;

export const DrawerOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    style={{ zIndex: zOverlay }}
    className={cn(
      "fixed inset-0 bg-bg-overlay/70 transition-opacity duration-200",
      "data-[state=open]:opacity-100 data-[state=closed]:opacity-0",
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = "DrawerOverlay";

const drawerVariants = cva(
  "fixed flex flex-col overflow-hidden border-border bg-bg-secondary text-fg-primary shadow-xl " +
    "transition-transform duration-[240ms] ease-out",
  {
    variants: {
      side: {
        right:
          "right-0 top-0 h-full border-l data-[state=closed]:translate-x-full data-[state=open]:translate-x-0",
        bottom:
          "bottom-0 left-0 right-0 w-full rounded-t-xl border-t data-[state=closed]:translate-y-full data-[state=open]:translate-y-0",
      },
      size: { sm: "", md: "", lg: "", full: "" },
    },
    compoundVariants: [
      // right side → width
      { side: "right", size: "sm", class: "w-full max-w-sm" },
      { side: "right", size: "md", class: "w-full max-w-md" },
      { side: "right", size: "lg", class: "w-full max-w-2xl" },
      { side: "right", size: "full", class: "w-full" },
      // bottom side → height
      { side: "bottom", size: "sm", class: "h-[40vh]" },
      { side: "bottom", size: "md", class: "h-[60vh]" },
      { side: "bottom", size: "lg", class: "h-[80vh]" },
      { side: "bottom", size: "full", class: "h-[95vh]" },
    ],
    defaultVariants: { side: "right", size: "md" },
  },
);

export type DrawerContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> &
  VariantProps<typeof drawerVariants>;

export const DrawerContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ side, size, className, children, onInteractOutside, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DialogPrimitive.Content
      ref={ref}
      style={{ zIndex: zModal }}
      className={cn(drawerVariants({ side, size }), className)}
      onInteractOutside={(e) => {
        // A toast sits outside every drawer. Pressing its button ("Undo") is
        // not clicking away — it used to close the drawer under the toast.
        if ((e.target as Element | null)?.closest?.("[data-toaster]")) e.preventDefault();
        onInteractOutside?.(e);
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DrawerPortal>
));
DrawerContent.displayName = "DrawerContent";

export const DrawerHeader = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("shrink-0 border-b border-border px-6 py-4", className)}
    {...props}
  />
));
DrawerHeader.displayName = "DrawerHeader";

export const DrawerBody = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex-1 overflow-y-auto px-6 py-5", className)} {...props} />
));
DrawerBody.displayName = "DrawerBody";

export const DrawerFooter = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "shrink-0 flex items-center justify-end gap-2 border-t border-border bg-bg-secondary px-6 py-4",
      className,
    )}
    {...props}
  />
));
DrawerFooter.displayName = "DrawerFooter";

export const DrawerTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-[22px] font-bold leading-tight tracking-tight", className)}
    {...props}
  />
));
DrawerTitle.displayName = "DrawerTitle";

export const DrawerDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-fg-muted", className)}
    {...props}
  />
));
DrawerDescription.displayName = "DrawerDescription";

/* --------------------------------------------------------------- DetailDrawer */

export type DetailDrawerStatus = Pick<
  StatusBadgeProps,
  "domain" | "value" | "label"
>;

export interface DetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Entity name (drawer H1, 22–24px / 700 per §9.5). */
  title: ReactNode;
  /** Optional breadcrumb trail above the title (e.g. Accounts / @username). */
  breadcrumbs?: BreadcrumbItem[];
  /** Optional status chip rendered next to the title. */
  status?: DetailDrawerStatus;
  /** Tab definitions; the drawer renders the Tabs UI and panels. */
  tabs: TabItem[];
  /** Controlled active tab. */
  activeTab?: string;
  onTabChange?: (value: string) => void;
  /** Sticky footer actions. */
  footer?: ReactNode;
  side?: DrawerContentProps["side"];
  size?: DrawerContentProps["size"];
}

/**
 * DetailDrawer — opinionated detail sheet (DESIGN-SYSTEM §5, §6.3).
 * Header (breadcrumbs + title + optional StatusBadge + close), a Tabs section,
 * scrollable body and a sticky footer for actions.
 */
export function DetailDrawer({
  open,
  onOpenChange,
  title,
  breadcrumbs,
  status,
  tabs,
  activeTab,
  onTabChange,
  footer,
  side = "right",
  size = "lg",
}: DetailDrawerProps) {
  const defaultValue = tabs[0]?.value;
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side={side} size={size}>
        <DrawerHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1.5">
              {breadcrumbs && breadcrumbs.length > 0 && (
                <Breadcrumbs items={breadcrumbs} />
              )}
              <div className="flex flex-wrap items-center gap-2">
                <DrawerTitle className="truncate">{title}</DrawerTitle>
                {status && (
                  <StatusBadge
                    domain={status.domain}
                    value={status.value}
                    label={status.label}
                  />
                )}
              </div>
            </div>
            <DrawerClose
              className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              aria-label="Закрыть"
            >
              <X size={18} strokeWidth={2} aria-hidden="true" />
            </DrawerClose>
          </div>
        </DrawerHeader>

        <Tabs
          value={activeTab}
          defaultValue={activeTab ? undefined : defaultValue}
          onValueChange={onTabChange}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="shrink-0 px-6 pt-3">
            <TabsList>
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <DrawerBody>
            {tabs.map((t) => (
              <TabsContent key={t.value} value={t.value} className="mt-0">
                {t.content}
              </TabsContent>
            ))}
          </DrawerBody>
        </Tabs>

        {footer && <DrawerFooter>{footer}</DrawerFooter>}
      </DrawerContent>
    </Drawer>
  );
}
DetailDrawer.displayName = "DetailDrawer";
